import { tx } from "../lib/i18n";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth, dashboardPath } from "../context/AuthContext";

export const AuthCard = ({ title, sub, children }) => (
  <div className="flex min-h-[70vh] items-center justify-center bg-gradient-to-b from-osk-light to-white px-5 py-12">
    <div className="osk-card osk-rise w-full max-w-md p-7 md:p-9">
      <h1 className="text-3xl">{title}</h1>
      {sub && <p className="mt-2 text-sm text-osk-text">{sub}</p>}
      <div className="mt-6">{children}</div>
    </div>
  </div>
);

export const Input = ({ label, testid, ...props }) => (
  <label className="block"><span className="osk-label">{label}</span><input className="osk-input" data-testid={testid} {...props} /></label>
);

export function Login() {
  const { t } = useI18n();
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", form);
      const u = await signIn(data);
      nav(params.get("next") || dashboardPath(u));
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <AuthCard title={t("auth.login")} sub={tx("Welkom terug bij Onskola.")}>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Input label={t("auth.email")} type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} testid="login-email-input" />
        <Input label={t("auth.password")} type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} testid="login-password-input" />
        <button className="osk-btn-primary w-full" disabled={busy} data-testid="login-submit-btn">{t("auth.login")}</button>
      </form>
      <p className="mt-6 text-center text-sm text-osk-text">{t("auth.no_account")} <Link to="/aanmelden" className="font-bold text-osk-blue" data-testid="goto-signup-link">{t("auth.signup")}</Link></p>
      <p className="mt-2 text-center text-sm text-osk-text">{t("auth.teacher_q")} <Link to="/docent-worden" className="font-bold text-osk-blue">{tx("Word docent")}</Link></p>
    </AuthCard>
  );
}

export function Signup() {
  const { t, lang } = useI18n();
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", accept_terms: false });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.accept_terms) return toast.error(tx("Accepteer de Algemene Voorwaarden voor Ouders om verder te gaan"));
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, language: lang });
      await signIn(data);
      toast.success(tx("Account aangemaakt! Voeg nu je kind(eren) toe."));
      nav("/ouder?tab=children");
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <AuthCard title={tx("Account voor ouders")} sub={tx("Maak een account aan, voeg je kinderen toe en boek live bijles. Kinderen hebben geen eigen account nodig.")}>
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        <Input label={t("auth.name")} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} testid="signup-name-input" />
        <Input label={t("auth.email")} type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} testid="signup-email-input" />
        <Input label={t("auth.password")} type="password" minLength={8} required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} testid="signup-password-input" />
        <label className="flex items-start gap-3 text-sm text-osk-text">
          <input type="checkbox" className="mt-1 h-4 w-4 accent-[#1565D8]" checked={form.accept_terms} onChange={(e) => setForm({ ...form, accept_terms: e.target.checked })} data-testid="signup-terms-checkbox" />
          <span>{t("auth.accept_parent")} <Link to="/voorwaarden/ouders" target="_blank" className="font-bold text-osk-blue">{tx("Algemene Voorwaarden voor Ouders")}</Link> {tx("en het")} <Link to="/privacy" target="_blank" className="font-bold text-osk-blue">{tx("Privacybeleid")}</Link>.</span>
        </label>
        <button className="osk-btn-primary w-full" disabled={busy} data-testid="signup-submit-btn">{t("auth.signup")}</button>
      </form>
      <p className="mt-6 text-center text-sm text-osk-text">{t("auth.have_account")} <Link to="/inloggen" className="font-bold text-osk-blue">{t("auth.login")}</Link></p>
    </AuthCard>
  );
}
